import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { SystemIcon, SystemIconButton } from '../../../../../Icon';
import { useDataTableContext } from '../../../../utils/DataTableContext';
import { OnVariablesChanged, SortModel } from '../../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickReportsContext } from '../../../../utils/DataTableQuickReportsContext';
import { OnQuickReportsChanged, QueryModel } from '../../../../utils/DataTableTypes';
import { ExportDialog } from '../dialogs/ExportDialog';
import { ScheduleDeleteExportDialog } from '../dialogs/ScheduleDeleteExportDialog';
import { ScheduleExportDialog } from '../dialogs/ScheduleExportDialog';
import { EditScheduleDesc } from '../panel/EditScheduleDesc';
import { QuickReportOrigin } from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import { DataTableDropdown, DataTableDropdownAction } from '../../../../DataTableAdvancedElements/DataTableRowActions';
import { Typography } from '../../../../../Typography/Typography';
import { useIntlUds } from '../../../../../../translations/UdsIntlProvider';
import { useDataTableQueryContext } from '../../../../utils/DataTableQueryProvider/DataTableQueryProvider';

interface Props<Q extends QueryModel, S extends SortModel> {
  report: OnQuickReportsChanged<Q, S> | undefined;
  onOpen: (() => void) | undefined;
  source: QuickReportOrigin;
  ExportDialogProps?: { children?: React.ReactNode };
  ScheduleExportDialogProps?: { children?: React.ReactNode };
}

export const DataTableExportActions = <Q extends QueryModel, S extends SortModel>({
  onOpen,
  report,
  source,
  ExportDialogProps,
  ScheduleExportDialogProps
}: Props<Q, S>) => {
  const { isFetching } = useDataTableContext();
  const { intl } = useIntlUds();
  const [openExportDialog, setOpenExportDialog] = useState<
    OnQuickReportsChanged<Q, S> | OnVariablesChanged<Q, S> | undefined
  >();
  const [openScheduleExportDialog, setOpenScheduleExportDialog] = useState<OnQuickReportsChanged<Q, S> | undefined>();
  const [openDeleteScheduleExportDialog, setOpenDeleteScheduleExportDialog] = useState<
    OnQuickReportsChanged<Q, S> | undefined
  >();
  const { unSavedQuickReport, exportFileFormats, onExport, onExportSchedule, onScheduleExportDelete } =
    useDataTableQuickReportsContext();
  const { queryVariables } = useDataTableQueryContext<Q, S>();

  const onDeleteSaveClose = useCallback(() => {
    setOpenDeleteScheduleExportDialog?.(undefined);
    setOpenScheduleExportDialog(undefined);
  }, []);

  const disableAllActions = !!unSavedQuickReport || isFetching;

  const newExportEnabled = !!queryVariables;
  const isQuickReportPanel = source === 'quick-report-panel' && !!report;

  // show dropdown with option only if we have selected quick report
  const shouldShowDropdown = !!report;
  // if we don't have selected report show export modal button
  const shouldShowExportModalBtn = !report;
  return (
    <>
      {shouldShowDropdown && (
        <DataTableDropdown
          actionsLabel={intl.DataTable.ExportActions.Export.label}
          iconName="upload"
          iconSize={isQuickReportPanel ? 'small' : 'medium'}
          dataTest={isQuickReportPanel ? `export-top-panel-for-${report?.name}` : 'export-toolbar'}
          disabled={disableAllActions}
          onOpen={onOpen}
        >
          {!!onExport && newExportEnabled && (
            <DataTableDropdownAction
              disabled={disableAllActions}
              key="export"
              dataTest="quick-report-export"
              iconName="upload"
              onClick={() => setOpenExportDialog(report || queryVariables)}
            >
              {intl.DataTable.QuickViews.action.export}
            </DataTableDropdownAction>
          )}
          {onExportSchedule && !report.schedule && (
            <DataTableDropdownAction
              disabled={disableAllActions}
              key="schedule-export"
              dataTest="schedule-export"
              iconName="calendar_today"
              onClick={() => setOpenScheduleExportDialog(report)}
            >
              <StyledScheduleExports>
                <span>{intl.DataTable.QuickViews.action.scheduleExport.label}</span>
                <StyledTypography variant="body2">
                  {intl.DataTable.QuickViews.action.scheduleExport.description}
                </StyledTypography>
              </StyledScheduleExports>
            </DataTableDropdownAction>
          )}
          {onScheduleExportDelete && !!report.schedule && (
            <DataTableDropdownAction
              disabled={disableAllActions}
              key="edit-export-schedule"
              dataTest="edit-scheduled-export"
              iconName="calendar_today"
              onClick={() => setOpenScheduleExportDialog(report)}
            >
              <StyledScheduleExports>
                <span>{intl.DataTable.QuickViews.action.editScheduleExport}</span>
                <StyledTypography variant="body2">
                  <SystemIcon name="repeat" size={14} />
                  <EditScheduleDesc report={report} />
                </StyledTypography>
              </StyledScheduleExports>
            </DataTableDropdownAction>
          )}
        </DataTableDropdown>
      )}
      {shouldShowExportModalBtn && (
        <SystemIconButton
          iconName="upload"
          disabled={disableAllActions || !queryVariables}
          dataTest="export-modal-btn"
          onClick={() => queryVariables && setOpenExportDialog(queryVariables)}
          tooltipText={intl.DataTable.ExportActions.Export.label}
        />
      )}
      {onExport && newExportEnabled && (
        <ExportDialog
          source={source}
          exportFileFormats={exportFileFormats}
          onClose={() => setOpenExportDialog(undefined)}
          report={openExportDialog}
          switchToScheduleExport={() => setOpenScheduleExportDialog(report)}
          key={!!openExportDialog ? 'open-export' : 'close-export'}
          onSave={onExport}
          {...ExportDialogProps}
        />
      )}
      {onExportSchedule && (
        <ScheduleExportDialog
          exportFileFormats={exportFileFormats}
          onClose={() => setOpenScheduleExportDialog(undefined)}
          report={openScheduleExportDialog}
          key={!!openScheduleExportDialog ? 'open-schedule-export' : 'close-schedule-export'}
          onSave={onExportSchedule}
          onExportDelete={() => setOpenDeleteScheduleExportDialog(report)}
          {...ScheduleExportDialogProps}
        />
      )}
      {onScheduleExportDelete && (
        <ScheduleDeleteExportDialog
          onSaveClose={onDeleteSaveClose}
          onClose={() => setOpenDeleteScheduleExportDialog(undefined)}
          report={openDeleteScheduleExportDialog}
          key={!!openDeleteScheduleExportDialog ? 'open-delete-schedule-export' : 'close-delete-schedule-export'}
          onSave={onScheduleExportDelete}
        />
      )}
    </>
  );
};

export const DataTableExportToolbarActions = DataTableExportActions;

const StyledScheduleExports = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  gap: ${({ theme }) => theme.spacingSize.s};
`;
